import { useCallback, useEffect, useRef, useState } from 'react'
import { useStytchB2BClient } from '@stytch/react/b2b'
import useAuthentication from '@/hooks/auth/useAuthentication'
import { ExportFile } from '@/services/export'
import { WorkflowStateDocumentCount } from '@/types/document-workflow-states'
import { ProjectModelVersion } from '@/types/project-models'
import generateUuid from '@/utils/uuid'

export type WebsocketData = {
  action: string
  updated_entity_ids: string[]
  error?: string
  model_version?: ProjectModelVersion
  export_response?: ExportFile
  workflow_state_counts?: WorkflowStateDocumentCount
}

const decodeJWT = (token: string) => {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  return JSON.parse(window.atob(base64))
}

export type UseWebsocketOptions = {
  /**
   * A function to handle incoming WebSocket messages.
   * It should be memoized to prevent unnecessary re-renders.
   *
   * @param event - The WebSocket message event.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMessage: (event: MessageEvent<any>) => void
}

export default function useWebsocket({ onMessage }: UseWebsocketOptions) {
  const { getFreshIdToken } = useAuthentication()
  const stytch = useStytchB2BClient()

  const attemptsCountRef = useRef(0)
  const pongTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearPongTimeout = useCallback(() => {
    if (!pongTimeoutRef.current) return
    clearTimeout(pongTimeoutRef.current)
    pongTimeoutRef.current = null
  }, [])

  const [websocket, setWebsocket] = useState<WebSocket | null>(null)
  const [websocketId, setWebsocketId] = useState<string | null>(null)
  const [tokenExpiration, setTokenExpiration] = useState<Date | null>(null)
  const [activeWebsockets, setActiveWebsockets] = useState<
    Record<string, WebSocket>
  >({})

  const openSocket = useCallback(
    async (useRefreshedToken = false) => {
      try {
        let token = ''
        if (useRefreshedToken) {
          token = await getFreshIdToken()
        } else {
          token = stytch.session.getTokens()?.session_jwt || ''
        }
        if (!token) throw new Error('No Stytch session found')

        setTokenExpiration(new Date(decodeJWT(token).exp * 1000))
        setWebsocket(
          new WebSocket(
            import.meta.env.VITE_PD_NOTIFICATION_SOCKET,

            token,
          ),
        )
        setWebsocketId(generateUuid())
      } catch (error) {
        console.error('Failed to open websocket:', error)
        setTokenExpiration(null)
        setWebsocket(null)
        setWebsocketId(null)
        setActiveWebsockets({})
      }
    },
    [getFreshIdToken, stytch.session],
  )

  useEffect(() => {
    openSocket()
  }, [openSocket])

  useEffect(() => {
    if (tokenExpiration) {
      const timeUntilExpiration = tokenExpiration.getTime() - Date.now()
      const timeout = setTimeout(() => {
        openSocket(true)
      }, timeUntilExpiration - 2 * 60 * 1000) // Refresh 2 min before expiration
      return () => clearTimeout(timeout)
    }
  }, [openSocket, tokenExpiration])

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>
    if (!websocket || !websocketId) return

    // Watchdog is per socket — several are open, and a shared timer lets them
    // cancel each other's.
    const ping = () => {
      if (websocket.readyState !== websocket.OPEN) return
      websocket.send('{"route": "/ping"}')
      pongTimeoutRef.current = setTimeout(() => websocket.close(), 15000)
    }

    const handleOpen = () => {
      // Adding this to help with debugging for now
      websocket.send(
        JSON.stringify({
          route: '/v2/ws-connection/get-id',
        }),
      )
      interval = setInterval(ping, 20000)
      setActiveWebsockets((prev) => {
        return { ...prev, [websocketId]: websocket }
      })
    }

    const handleClose = (event: CloseEvent) => {
      if (event.wasClean || attemptsCountRef.current < 2) {
        openSocket()
        !event.wasClean && attemptsCountRef.current++
      } else {
        attemptsCountRef.current = 0
      }
    }

    websocket.addEventListener('open', handleOpen)
    websocket.addEventListener('close', handleClose)

    return () => {
      interval && clearInterval(interval)
      // Keeps a pending watchdog from closing the socket that replaces this one.
      clearPongTimeout()
      websocket.removeEventListener('open', handleOpen)
      websocket.removeEventListener('close', handleClose)
    }
  }, [clearPongTimeout, openSocket, websocket, websocketId])

  // Closes the replaced socket — only the newest one has listeners. Must stay
  // below the effect above, whose cleanup detaches handleClose; otherwise this
  // triggers a reconnect.
  useEffect(() => {
    if (!websocket) return
    return () => websocket.close()
  }, [websocket])

  useEffect(() => {
    if (!websocket) return

    const handleMessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data)
      const { action } = data as WebsocketData

      if (action === 'pong') {
        clearPongTimeout()
        return
      }

      onMessage(event)
    }
    websocket.addEventListener('message', handleMessage)

    return () => {
      websocket.removeEventListener('message', handleMessage)
    }
  }, [clearPongTimeout, onMessage, websocket])

  useEffect(() => {
    setActiveWebsockets((prev) => {
      if (
        Object.keys(prev).length > 1 &&
        websocketId &&
        Object.hasOwn(prev, websocketId)
      ) {
        Object.entries(prev).map(([wsId, ws]) => {
          if (wsId !== websocketId) {
            ws.close()
          }
        })
        return { [websocketId]: prev[websocketId] }
      }
      return prev
    })
  }, [activeWebsockets, websocketId])

  return websocketId ? activeWebsockets[websocketId] : null
}
